export type WireColor = 'live' | 'neutral' | 'earth';

export type ComponentType =
  | 'supply' |'lamp' |'switch-one-way' |'switch-two-way' |'socket' |'junction-box' |'mcb' |'rcbo' |'fuse' |'neutral-bar' |'earth-bar';

export interface Terminal {
  id: string;
  label: string;
  color: WireColor;
  relX: number; // relative to component center
  relY: number;
}

export interface PlacedComponent {
  instanceId: string;
  type: ComponentType;
  x: number;
  y: number;
  label: string;
  switchState?: boolean; // for switches
}

export interface Wire {
  id: string;
  fromComponentId: string;
  fromTerminalId: string;
  toComponentId: string;
  toTerminalId: string;
  color: WireColor;
}

export type CircuitStatus =
  | 'idle' |'correct' |'incorrect' |'unsafe' |'short-circuit' |'incomplete';

export interface CircuitFault {
  severity: 'error' | 'warning' | 'info';
  code: string;
  title: string;
  message: string;
}

export interface CircuitResult {
  status: CircuitStatus;
  faults: CircuitFault[];
  canSimulate: boolean;
}

export const COMPONENT_DEFINITIONS: Record<
  ComponentType,
  { label: string; width: number; height: number; terminals: Omit<Terminal, 'id'>[] }
> = {
  supply: {
    label: 'Supply',
    width: 70,
    height: 70,
    terminals: [
      { label: 'L', color: 'live', relX: 35, relY: 0 },
      { label: 'N', color: 'neutral', relX: 35, relY: 70 },
      { label: 'E', color: 'earth', relX: 0, relY: 35 },
    ],
  },
  lamp: {
    label: 'Lamp',
    width: 60,
    height: 60,
    terminals: [
      { label: 'L', color: 'live', relX: 0, relY: 30 },
      { label: 'N', color: 'neutral', relX: 60, relY: 30 },
      { label: 'E', color: 'earth', relX: 30, relY: 60 },
    ],
  },
  'switch-one-way': {
    label: '1-Way Switch',
    width: 60,
    height: 50,
    terminals: [
      { label: 'COM', color: 'live', relX: 0, relY: 25 },
      { label: 'L1', color: 'live', relX: 60, relY: 25 },
    ],
  },
  'switch-two-way': {
    label: '2-Way Switch',
    width: 70,
    height: 60,
    terminals: [
      { label: 'COM', color: 'live', relX: 0, relY: 30 },
      { label: 'L1', color: 'live', relX: 70, relY: 15 },
      { label: 'L2', color: 'live', relX: 70, relY: 45 },
    ],
  },
  socket: {
    label: 'Socket',
    width: 65,
    height: 65,
    terminals: [
      { label: 'L', color: 'live', relX: 0, relY: 20 },
      { label: 'N', color: 'neutral', relX: 0, relY: 45 },
      { label: 'E', color: 'earth', relX: 32, relY: 65 },
    ],
  },
  'junction-box': {
    label: 'Junction Box',
    width: 60,
    height: 60,
    terminals: [
      { label: 'L', color: 'live', relX: 30, relY: 0 },
      { label: 'N', color: 'neutral', relX: 60, relY: 30 },
      { label: 'E', color: 'earth', relX: 30, relY: 60 },
      { label: 'L2', color: 'live', relX: 0, relY: 30 },
    ],
  },
  mcb: {
    label: 'MCB',
    width: 45,
    height: 70,
    terminals: [
      { label: 'IN', color: 'live', relX: 22, relY: 0 },
      { label: 'OUT', color: 'live', relX: 22, relY: 70 },
    ],
  },
  rcbo: {
    label: 'RCBO',
    width: 55,
    height: 80,
    terminals: [
      { label: 'L-IN', color: 'live', relX: 15, relY: 0 },
      { label: 'N-IN', color: 'neutral', relX: 40, relY: 0 },
      { label: 'L-OUT', color: 'live', relX: 15, relY: 80 },
      { label: 'N-OUT', color: 'neutral', relX: 40, relY: 80 },
    ],
  },
  fuse: {
    label: 'Fuse',
    width: 50,
    height: 30,
    terminals: [
      { label: 'IN', color: 'live', relX: 0, relY: 15 },
      { label: 'OUT', color: 'live', relX: 50, relY: 15 },
    ],
  },
  'neutral-bar': {
    label: 'Neutral Bar',
    width: 80,
    height: 30,
    terminals: [
      { label: 'N1', color: 'neutral', relX: 10, relY: 0 },
      { label: 'N2', color: 'neutral', relX: 30, relY: 0 },
      { label: 'N3', color: 'neutral', relX: 50, relY: 0 },
      { label: 'N4', color: 'neutral', relX: 70, relY: 0 },
    ],
  },
  'earth-bar': {
    label: 'Earth Bar',
    width: 80,
    height: 30,
    terminals: [
      { label: 'E1', color: 'earth', relX: 10, relY: 30 },
      { label: 'E2', color: 'earth', relX: 30, relY: 30 },
      { label: 'E3', color: 'earth', relX: 50, relY: 30 },
      { label: 'E4', color: 'earth', relX: 70, relY: 30 },
    ],
  },
};

export const WIRE_COLOR_MAP: Record<WireColor, { stroke: string; label: string; hex: string }> = {
  live: { stroke: '#b45309', label: 'Live (Brown)', hex: '#b45309' },
  neutral: { stroke: '#1d4ed8', label: 'Neutral (Blue)', hex: '#1d4ed8' },
  earth: { stroke: '#15803d', label: 'Earth (Green/Yellow)', hex: '#15803d' },
};